import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { API, authHeaders, formatErr } from "./admin/api";
import ContactTab from "./admin/ContactTab";
import PricesTab from "./admin/PricesTab";
import WorkTab from "./admin/WorkTab";
import FaqsTab from "./admin/FaqsTab";
import QuikpikTab from "./admin/QuikpikTab";
import LeadsTab from "./admin/LeadsTab";
import AnnouncementTab from "./admin/AnnouncementTab";
import VersionsTab from "./admin/VersionsTab";
import SecurityTab from "./admin/SecurityTab";

const TABS = [
  { key: "contact", label: "Contact & Business" },
  { key: "prices", label: "Prices" },
  { key: "work", label: "Work / Clients" },
  { key: "faqs", label: "FAQs" },
  { key: "quikpik", label: "Quikpik" },
  { key: "leads", label: "Leads", badge: true },
  { key: "announcement", label: "Announcement" },
  { key: "versions", label: "Versions" },
  { key: "security", label: "Security" },
];

const inputCls =
  "w-full border border-line bg-base px-4 py-3 text-sm text-txt outline-none transition-colors duration-300 focus:border-myrtle";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("allcov_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("contact");
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    document.title = "Admin — Allcov";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
      document.title = "Allcov LLP — We Run Your Business. You Grow It.";
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/content`)
      .then((r) => setContent(r.data))
      .catch(() => toast.error("Couldn't load current content."));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const fetchCount = () =>
      axios
        .get(`${API}/leads/new-count`, authHeaders(token))
        .then((r) => setNewCount(r.data.count))
        .catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => clearInterval(t);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("allcov_admin_token", data.token);
      setToken(data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatErr(err, "Login failed."));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("allcov_admin_token");
    setToken(null);
    setContent(null);
  };

  const onUnauthorized = () => {
    toast.error("Session expired. Log in again.");
    logout();
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pt-20" data-testid="admin-login">
        <form onSubmit={login} className="inner-glow w-full max-w-md border border-line bg-surface p-8 md:p-10">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center border border-line bg-base">
              <Lock size={18} className="text-pop" />
            </span>
            <div>
              <p className="font-batangas text-2xl tracking-tight">Allcov Admin</p>
              <p className="text-xs uppercase tracking-[0.2em] text-faint">Restricted Access</p>
            </div>
          </div>
          <div className="mt-8 space-y-6">
            <div>
              <label htmlFor="admin-email" className="text-sm text-mut">Email</label>
              <input
                id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin email" data-testid="admin-email-input" className={`${inputCls} mt-2`}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-sm text-mut">Password</label>
              <div className="relative">
                <input
                  id="admin-password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="password" data-testid="admin-password-input" className={`${inputCls} mt-2 pr-12`}
                />
                <button
                  type="button" onClick={() => setShowPassword(!showPassword)}
                  data-testid="admin-password-toggle" aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 mt-1 -translate-y-1/2 text-mut transition-colors duration-300 hover:text-pop"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={loading} data-testid="admin-login-button"
              className="flex w-full items-center justify-center gap-2 bg-myrtle px-6 py-4 text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-myrtle-light disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Log In"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-mut" data-testid="admin-loading">
        Loading…
      </div>
    );
  }

  const tabProps = { content, token, onSaved: setContent, onUnauthorized };

  return (
    <div className="mx-auto max-w-5xl px-6 pb-24 pt-32 lg:pt-40" data-testid="admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-batangas text-3xl tracking-tight md:text-4xl">Content Manager</p>
          <p className="mt-1 text-xs uppercase tracking-[0.2em] text-faint">Changes go live on save</p>
        </div>
        <button
          onClick={logout} data-testid="admin-logout-button"
          className="flex items-center gap-2 border border-line px-6 py-3 text-xs uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop"
        >
          <LogOut size={14} /> Logout
        </button>
      </div>

      <nav className="mt-8 flex gap-2 overflow-x-auto pb-2" aria-label="Admin sections" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`relative shrink-0 whitespace-nowrap border px-4 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
              tab === t.key ? "border-myrtle bg-myrtle/10 text-pop" : "border-line text-mut hover:text-txt"
            }`}
          >
            {t.label}
            {t.badge && newCount > 0 && (
              <span data-testid="leads-badge" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-myrtle px-1 text-[10px] font-bold text-white">
                {newCount}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="mt-6">
        {tab === "contact" && <ContactTab {...tabProps} />}
        {tab === "prices" && <PricesTab {...tabProps} />}
        {tab === "work" && <WorkTab {...tabProps} />}
        {tab === "faqs" && <FaqsTab {...tabProps} />}
        {tab === "quikpik" && <QuikpikTab {...tabProps} />}
        {tab === "leads" && <LeadsTab token={token} onUnauthorized={onUnauthorized} onLeadsSeen={() => {}} />}
        {tab === "announcement" && <AnnouncementTab {...tabProps} />}
        {tab === "versions" && <VersionsTab token={token} onSaved={setContent} onUnauthorized={onUnauthorized} />}
        {tab === "security" && <SecurityTab token={token} onUnauthorized={onUnauthorized} />}
      </div>
    </div>
  );
}
